import React, { useState, useRef, useCallback, useEffect } from "react";
import { Box, Stack } from "@mui/material";
import { toast } from "react-toastify";
import { CustomModal } from "@/components/Modal";
import { Button, ButtonVariantOption } from "@/components/Button";
import { PageTitle } from "@/components/Title";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { AddressListGrid } from "@/features/dmManagement/components/AddressListGrid";
import { AddressListUpload } from "@/features/dmManagement/components/AddressListUpload";
import { PostAddressListParseAPI } from "@/features/dmManagement/api/postAddressListParseAPI";
import { PostAddressListReuploadAPI } from "@/features/dmManagement/api/postAddressListReuploadAPI";
import { extractFileError } from "@/features/dmManagement/utils/extractFileError";
import { useApiClient } from "@/hooks/useApiClient";
import type {
  Order,
  ParseResult,
  ApiCallStatus,
} from "@/features/dmManagement/types";
import type { KeyedMutator } from "swr";

interface ReuploadDialogProps {
  open: boolean;
  onClose: () => void;
  orderId: number;
  orderMutate: KeyedMutator<Order>;
}

/**
 * 宛先リスト再アップロードダイアログ
 * 宛先リストの再アップロード・パース・プレビュー確認・送信を行うモーダルダイアログ
 */
export const ReuploadDialog: React.FC<ReuploadDialogProps> = ({
  open,
  onClose,
  orderId,
  orderMutate,
}) => {
  const { apiClient } = useApiClient();
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseStatus, setParseStatus] = useState<ApiCallStatus>("idle");
  const [parseError, setParseError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const parseAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) {
      setFile(null);
      setParseResult(null);
      setParseStatus("idle");
      setParseError(null);
      setIsSubmitting(false);
      setFileInputKey((prev) => prev + 1);
      parseAbortRef.current?.abort();
      parseAbortRef.current = null;
    }
  }, [open]);

  const canSubmit =
    parseStatus === "success" &&
    parseResult != null &&
    !parseResult.hasError &&
    !isSubmitting;

  const executeParse = useCallback(
    async (targetFile: File): Promise<void> => {
      parseAbortRef.current?.abort();

      const controller = new AbortController();
      parseAbortRef.current = controller;

      setParseResult(null);
      setParseError(null);
      setParseStatus("loading");

      try {
        const result = await PostAddressListParseAPI(
          apiClient,
          targetFile,
          controller.signal
        );

        if (controller.signal.aborted) return;

        setParseResult(result);
        setParseStatus("success");
      } catch (error) {
        if (controller.signal.aborted) return;

        setParseStatus("error");
        const message =
          extractFileError(error) ?? "宛先リストのパースに失敗しました";
        setParseError(message);
        toast.error(message);
      }
    },
    [apiClient]
  );

  const handleFileSelect = useCallback(
    (selectedFile: File): void => {
      setFile(selectedFile);
      void executeParse(selectedFile);
    },
    [executeParse]
  );

  const handleFileRejected = useCallback((): void => {
    parseAbortRef.current?.abort();
    setFile(null);
    setParseResult(null);
    setParseStatus("idle");
    setParseError(null);
  }, []);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!file || !canSubmit) return;

    setIsSubmitting(true);
    try {
      await PostAddressListReuploadAPI(apiClient, orderId, file);
      await orderMutate();
      toast.success("宛先リストを再アップロードしました");
      onClose();
    } catch {
      toast.error("再アップロードに失敗しました");
    } finally {
      setIsSubmitting(false);
    }
  }, [file, canSubmit, apiClient, orderId, orderMutate, onClose]);

  const handleModalClose = (
    _event: Record<string, unknown>,
    _reason: "backdropClick" | "escapeKeyDown"
  ): void => {
    onClose();
  };

  return (
    <CustomModal
      isOpen={open}
      handleClose={handleModalClose}
      ariaLabelledby="reupload-dialog"
      ariaDescribedby="reupload-dialog-description"
      width={"1200px"}
      maxHeight={"90vh"}
      overflow="auto"
    >
      <Stack direction={"column"} spacing={3}>
        <PageTitle>宛先リスト再アップロード</PageTitle>
        <Box>
          {/* key変更でファイル入力をリセット（DOMのvalueクリアのため再マウント） */}
          <AddressListUpload
            key={fileInputKey}
            onFileSelect={handleFileSelect}
            onFileRejected={handleFileRejected}
            parseStatus={parseStatus}
            parseError={parseError}
            showTitle={false}
          />

          {parseResult && (
            <AddressListGrid
              rows={parseResult.rows}
              totalRows={parseResult.totalRows}
              hasError={parseResult.hasError}
              hasWarning={parseResult.hasWarning}
            />
          )}
        </Box>
        <Stack
          direction={"row"}
          spacing={2}
          marginTop={2}
          justifyContent={"center"}
        >
          <Button
            label="キャンセル"
            onClick={onClose}
            disabled={isSubmitting}
            size={SizingWrapperStyle.INHERIT}
            variant={ButtonVariantOption.Outlined}
          />
          <Button
            label="再アップロードする"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!canSubmit}
            size={SizingWrapperStyle.INHERIT}
            variant={ButtonVariantOption.Contained}
            hasLoading={true}
            loading={isSubmitting}
          />
        </Stack>
      </Stack>
    </CustomModal>
  );
};
